import React, { createContext, useContext, useReducer, useEffect } from 'react'

const ConfigurationContext = createContext()

const initialState = {
  selectedProvider: null,
  vcpus: 2,
  ramGb: 4,
  storageGb: 100,
  gdprCompliant: false,
  nonUsCloudAct: false,
  instanceType: 'Medium',
  costData: null,
  providers: [],
  instanceTypes: [],
  loading: false,
  error: null
}

function configurationReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'SET_PROVIDERS':
      return { ...state, providers: action.payload }
    case 'SET_INSTANCE_TYPES':
      return { ...state, instanceTypes: action.payload }
    case 'SET_SELECTED_PROVIDER':
      return { ...state, selectedProvider: action.payload }
    case 'SET_VCPUS':
      return { ...state, vcpus: action.payload }
    case 'SET_RAM':
      return { ...state, ramGb: action.payload }
    case 'SET_STORAGE':
      return { ...state, storageGb: action.payload }
    case 'SET_GDPR_COMPLIANT':
      return { ...state, gdprCompliant: action.payload }
    case 'SET_NON_US_CLOUD_ACT':
      return { ...state, nonUsCloudAct: action.payload }
    case 'SET_INSTANCE_TYPE':
      const instanceType = state.instanceTypes.find(t => t.name === action.payload)
      return { 
        ...state, 
        instanceType: action.payload,
        vcpus: instanceType?.vcpus || state.vcpus,
        ramGb: instanceType?.ramGb || state.ramGb
      }
    case 'SET_COST_DATA':
      return { ...state, costData: action.payload }
    case 'RESET_CONFIGURATION':
      return { ...initialState, providers: state.providers, instanceTypes: state.instanceTypes }
    default:
      return state
  }
}

export function ConfigurationProvider({ children }) {
  const [state, dispatch] = useReducer(configurationReducer, initialState)

  // Load initial data
  useEffect(() => {
    loadProviders()
    loadInstanceTypes()
  }, [])

  const loadProviders = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const response = await fetch('/api/providers')
      if (!response.ok) throw new Error('Failed to load providers')
      const providers = await response.json()
      dispatch({ type: 'SET_PROVIDERS', payload: providers })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const loadInstanceTypes = async () => {
    try {
      const response = await fetch('/api/instance-types')
      if (!response.ok) throw new Error('Failed to load instance types')
      const instanceTypes = await response.json()
      dispatch({ type: 'SET_INSTANCE_TYPES', payload: instanceTypes })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const loadFilteredProviders = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const params = new URLSearchParams()
      if (state.gdprCompliant) params.append('gdpr', 'true')
      if (state.nonUsCloudAct) params.append('nonUsCloudAct', 'true')
      
      const response = await fetch(`/api/providers?${params}`)
      if (!response.ok) throw new Error('Failed to load providers')
      const providers = await response.json()
      dispatch({ type: 'SET_PROVIDERS', payload: providers })
      
      // Reset selected provider if it's not in the filtered list
      if (state.selectedProvider && !providers.find(p => p.id === state.selectedProvider.id)) {
        dispatch({ type: 'SET_SELECTED_PROVIDER', payload: null })
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const calculateCost = async () => {
    if (!state.selectedProvider) return

    try {
      const response = await fetch('/api/calculate-cost', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          providerId: state.selectedProvider.id,
          vcpus: state.vcpus,
          ramGb: state.ramGb,
          storageGb: state.storageGb,
        }),
      })

      if (!response.ok) throw new Error('Failed to calculate cost')
      const costData = await response.json()
      dispatch({ type: 'SET_COST_DATA', payload: costData })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const saveConfiguration = async (name) => {
    try {
      const configuration = {
        name,
        provider: state.selectedProvider,
        vcpus: state.vcpus,
        ramGb: state.ramGb,
        storageGb: state.storageGb,
        instanceType: state.instanceType,
        gdprCompliant: state.gdprCompliant,
        nonUsCloudAct: state.nonUsCloudAct,
        costData: state.costData,
      }

      const response = await fetch('/api/configurations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configuration),
      })

      if (!response.ok) throw new Error('Failed to save configuration')
      const savedConfig = await response.json()
      return savedConfig
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    }
  }

  const exportConfiguration = () => {
    const configuration = {
      provider: state.selectedProvider?.name,
      vcpus: state.vcpus,
      ramGb: state.ramGb,
      storageGb: state.storageGb,
      instanceType: state.instanceType,
      costMonthly: state.costData?.monthly,
      compliance: {
        gdpr: state.gdprCompliant,
        nonUsCloudAct: state.nonUsCloudAct,
      },
      exportedAt: new Date().toISOString(),
    }

    const blob = new Blob([JSON.stringify(configuration, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cloud-config-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const value = {
    state,
    dispatch,
    loadFilteredProviders,
    calculateCost,
    saveConfiguration,
    exportConfiguration,
  }

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  )
}

export function useConfiguration() {
  const context = useContext(ConfigurationContext)
  if (!context) {
    throw new Error('useConfiguration must be used within a ConfigurationProvider')
  }
  return context
} 